import React, { useMemo, useState } from 'react';
import { styled } from '@abyss/web/tools/styled';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';

const MenuContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '0px',
  padding: '0',
  margin: '0',
});

const MenuButton = styled('button', {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  gap: '8px',
  border: 'none',
  borderRadius: '0',
  cursor: 'pointer',
  padding: '12px 8px 12px 8px',
  margin: '0',
  minHeight: '48px',
  fontSize: '16px',
  fontWeight: 700,
  backgroundColor: '#ffffff',
  color: '$primary1',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  textAlign: 'left',
  lineHeight: 1.2,
  flexWrap: 'nowrap',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgb(25, 110, 207)',
    color: '#ffffff',
  },
  '&[aria-current="page"]': {
    backgroundColor: 'rgb(25, 110, 207)',
    color: '#ffffff',
  },
});

const IconWrap = styled('div', {
  width: '24px',
  minWidth: '24px',
  maxWidth: '24px',
  height: '24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  flexShrink: 0,
  '& *': {
    margin: '0 !important',
    padding: '0 !important',
  },
});

const SubMenuContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: '16px',
});

const ExpandIcon = styled('span', {
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
});

const IconSlot = styled('span', {
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

export const LeftMenu = () => {
  const { navigate, getLocation } = useRouter();
  const pathname = getLocation()?.pathname || '';
  const search = getLocation()?.search || '';
  const isDashboardActive = pathname === '/' || pathname === '/route-tool/dashboard';
  const isUploadActive = pathname.startsWith('/route-tool/upload');
  const isSearchActive = pathname.startsWith('/route-tool/search');
  const isTemplatesActive = pathname.startsWith('/route-tool/templates');
  const isAdminActive = pathname.startsWith('/route-tool/add-entity');

  const [isManageEntityExpanded, setIsManageEntityExpanded] = useState(isAdminActive);

  const activeAdminTab = useMemo(() => {
    const params = new URLSearchParams(search);
    const tab = String(params.get('tab') || '').trim().toLowerCase();
    if (tab === 'remove' || tab === 'history') {
      return 'history';
    }
    return 'add';
  }, [search]);

  const isAddEntityActive = isAdminActive && activeAdminTab === 'add';
  const isRemoveEntityActive = isAdminActive && activeAdminTab === 'history';
  const isManageEntityParentActive = isAdminActive && !isAddEntityActive && !isRemoveEntityActive;

  return (
    <MenuContainer>
      <MenuButton
        onClick={() => navigate('/')}
        aria-current={isDashboardActive ? 'page' : undefined}
        data-testid="left-menu-dashboard"
      >
        <IconWrap>
          <IconSymbol
            icon={'dashboard' as const}
            color="$primary1"
            size={24}
            variant={'filled' as const}
          />
        </IconWrap>
        Dashboard
      </MenuButton>

      <MenuButton
        onClick={() => navigate('/route-tool/upload')}
        aria-current={isUploadActive ? 'page' : undefined}
        data-testid="left-menu-upload"
      >
        <IconWrap>
          <IconSymbol
            icon={'upload_file' as const}
            color="$primary1"
            size={24}
            variant={'filled' as const}
          />
        </IconWrap>
        Upload
      </MenuButton>

      <MenuButton
        onClick={() => navigate('/route-tool/search')}
        aria-current={isSearchActive ? 'page' : undefined}
        data-testid="left-menu-search"
      >
        <IconWrap>
          <IconSymbol
            icon={'search' as const}
            color="$primary1"
            size={24}
            variant={'filled' as const}
          />
        </IconWrap>
        Search
      </MenuButton>

      <MenuButton
        onClick={() => navigate('/route-tool/templates')}
        aria-current={isTemplatesActive ? 'page' : undefined}
        data-testid="left-menu-templates"
      >
        <IconWrap>
          <IconSymbol
            icon={'download' as const}
            color="$primary1"
            size={24}
            variant={'filled' as const}
          />
        </IconWrap>
        File Templates
      </MenuButton>

      <MenuButton
        onClick={() => setIsManageEntityExpanded(!isManageEntityExpanded)}
        aria-current={isManageEntityParentActive ? 'page' : undefined}
        aria-expanded={isManageEntityExpanded}
        data-testid="left-menu-manage-entity"
      >
        <IconWrap>
          <IconSymbol
            icon={'settings' as const}
            color="$primary1"
            size={24}
            variant={'filled' as const}
          />
        </IconWrap>
        Manage Entity
        <ExpandIcon>
          <IconSymbol
            icon={isManageEntityExpanded ? 'expand_less' : 'expand_more'}
            color="$primary1"
            size={20}
            variant={'filled' as const}
          />
        </ExpandIcon>
      </MenuButton>

      {isManageEntityExpanded && (
        <SubMenuContainer>
          <MenuButton
            onClick={() => navigate('/route-tool/add-entity?tab=add')}
            aria-current={isAddEntityActive ? 'page' : undefined}
            data-testid="left-menu-add-entity"
          >
            <IconWrap>
              <IconSymbol
                icon={'add' as const}
                color="$primary1"
                size={24}
                variant={'filled' as const}
              />
            </IconWrap>
            Add/Remove Entity
          </MenuButton>

          {/* Removed History menu item — hidden; uncomment to re-enable
          <MenuButton
            onClick={() => navigate('/route-tool/add-entity?tab=history')}
            aria-current={isRemoveEntityActive ? 'page' : undefined}
            data-testid="left-menu-remove-entity"
          >
            <IconWrap>
              <IconSymbol
                icon={'delete' as const}
                color="$primary1"
                size={24}
                variant={'filled' as const}
              />
            </IconWrap>
            Removed History
          </MenuButton>
          */}
        </SubMenuContainer>
      )}
    </MenuContainer>
  );
};
